import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CircleDot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import ListFilterBar from '../components/work/ListFilterBar';
import { PageFrame } from '../components/work/page-chrome';
import { BrandEmpty, ErrorAlert, ListEmpty, LoadingRows, PageTitle } from '../components/work/page-states';
import { useDirectoryQuery } from '../components/work/use-directory-query';
import { MonoKey, titleLinkClass } from '../components/work/table-cells';
import { apiFetch, ApiError, v1Path } from '../lib/api';
import { compareText, matchesQuery, selectedAllows } from '../lib/directory-filter';
import { formatTimestampDateTime } from '../lib/dates';
import type { ProjectDto } from '../lib/work-types';

const WEBHOOK_CHIPS = ['status'] as const;
const WEBHOOK_DEFAULT_ORDER = '-created_at';
const WEBHOOK_SORT_OPTIONS = [
  { value: '-created_at', label: 'Newest' },
  { value: 'created_at', label: 'Oldest' },
  { value: 'url', label: 'URL A-Z' },
] as const;

const EVENTS = [
  'work_item.created',
  'work_item.updated',
  'work_item.status_changed',
  'work_item.assigned',
  'work_item.archived',
  'work_item.deleted',
  'comment.created',
  'comment.updated',
  'comment.deleted',
  'project.created',
  'project.updated',
  'project.deleted',
  'webhook.delivery_failed',
  'release.created',
  'release.updated',
] as const;

type WebhookDto = {
  id: string;
  url: string;
  events: string[];
  project_ids: string[] | null;
  is_active: boolean;
  created_at: string;
  disabled_at: string | null;
  secret?: string;
};

type DeliveryDto = {
  id: string;
  event_id: string;
  delivery_id: string;
  event: string;
  status: string;
  attempt: number;
  response_status: number | null;
  created_at: string;
};

function EventPicker({
  selected,
  onToggle,
}: {
  selected: string[];
  onToggle: (event: string) => void;
}) {
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {EVENTS.map((event) => (
        <label key={event} className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={selected.includes(event)}
            onCheckedChange={() => onToggle(event)}
          />
          <span className="font-mono text-xs">{event}</span>
        </label>
      ))}
    </div>
  );
}

export default function Webhooks() {
  const { id } = useParams<{ id?: string }>();
  if (id) return <WebhookDetail id={id} />;
  return <WebhookList />;
}

function WebhookList() {
  const filters = useDirectoryQuery(WEBHOOK_CHIPS, WEBHOOK_DEFAULT_ORDER);
  const [items, setItems] = useState<WebhookDto[]>([]);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['work_item.created']);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [secret, setSecret] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [hooks, projectRes] = await Promise.all([
        apiFetch<{ items: WebhookDto[] }>(v1Path('/webhooks')),
        apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0')),
      ]);
      setItems(hooks.items);
      setProjects(projectRes.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load webhooks');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    const rows = items.filter((hook) => {
      const status = hook.is_active ? 'active' : 'disabled';
      if (!selectedAllows(filters.selected.status, status)) return false;
      return matchesQuery(filters.query, [hook.url, hook.events.join(' ')]);
    });
    const orderBy = filters.orderBy ?? WEBHOOK_DEFAULT_ORDER;
    return rows.slice().sort((left, right) => {
      if (orderBy === 'created_at') return left.created_at.localeCompare(right.created_at);
      if (orderBy === 'url') return compareText(left.url, right.url);
      return right.created_at.localeCompare(left.created_at);
    });
  }, [filters.orderBy, filters.query, filters.selected.status, items]);

  function toggleEvent(event: string) {
    setEvents((current) => (
      current.includes(event) ? current.filter((e) => e !== event) : [...current, event]
    ));
  }

  function toggleProject(projectId: string) {
    setProjectIds((current) => (
      current.includes(projectId) ? current.filter((id) => id !== projectId) : [...current, projectId]
    ));
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const created = await apiFetch<WebhookDto>(v1Path('/webhooks'), {
        method: 'POST',
        body: JSON.stringify({
          url: url.trim(),
          events,
          project_ids: projectIds.length > 0 ? projectIds : undefined,
        }),
      });
      setSecret(created.secret ?? null);
      setUrl('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageFrame>
      <div className="flex flex-1 flex-col gap-4">
      <PageTitle title="Webhooks" count={loading || error ? undefined : visible.length} />
      <p className="text-sm text-muted-foreground">Signed outbound events. Secret is shown once.</p>
      <ListFilterBar
        query={filters.query}
        onQueryChange={(query) => filters.apply({ query })}
        searchPlaceholder="Search URL or event..."
        searchAriaLabel="Search webhooks"
        chips={[
          {
            id: 'status',
            label: 'Status',
            icon: CircleDot,
            options: [
              { value: 'active', label: 'Active' },
              { value: 'disabled', label: 'Disabled' },
            ],
            selected: filters.selected.status,
            onToggle: (value) => filters.toggleChip('status', value),
          },
        ]}
        sort={{
          value: filters.orderBy ?? WEBHOOK_DEFAULT_ORDER,
          options: [...WEBHOOK_SORT_OPTIONS],
          onChange: (value) => filters.apply({ orderBy: value }),
        }}
        onClear={filters.clear}
      />
      {error ? <ErrorAlert>{error}</ErrorAlert> : null}
      {secret ? (
        <div className="mb-4 border-t py-3">
          <p className="text-sm font-medium">Copy this signing secret now. It will not be shown again.</p>
          <code className="mt-1 block break-all font-mono text-xs">{secret}</code>
          <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setSecret(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}
      <form onSubmit={onCreate} className="mb-6 max-w-2xl space-y-4">
        <div className="grid gap-2">
          <Label htmlFor="hook-url">URL</Label>
          <Input
            id="hook-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
          />
        </div>
        <div className="grid gap-2">
          <Label>Events</Label>
          <EventPicker selected={events} onToggle={toggleEvent} />
        </div>
        {projects.length > 0 ? (
          <div className="grid gap-2">
            <Label>Streams (omit for all)</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              {projects.map((project) => (
                <label key={project.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={projectIds.includes(project.id)}
                    onCheckedChange={() => toggleProject(project.id)}
                  />
                  <span>{project.identifier} {project.name}</span>
                </label>
              ))}
            </div>
          </div>
        ) : null}
        <Button type="submit" size="sm" disabled={saving || events.length === 0}>
          {saving ? 'Creating...' : 'Create webhook'}
        </Button>
      </form>
      {loading ? (
        <LoadingRows columns={3} />
      ) : items.length === 0 ? (
        <BrandEmpty title="No webhooks yet." />
      ) : visible.length === 0 ? (
        <ListEmpty
          title="No webhooks match"
          description="Clear filters if you expected matches."
          action={
            <Button type="button" variant="outline" size="sm" onClick={filters.clear}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-0 w-[40%]">URL</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead className="hidden min-w-0 md:table-cell">Events</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((hook) => (
              <TableRow key={hook.id}>
                <TableCell className="min-w-0">
                  <Link to={`/settings/webhooks/${hook.id}`} className={`${titleLinkClass} truncate break-all`}>
                    {hook.url}
                  </Link>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {hook.is_active ? 'Active' : 'Disabled'}
                </TableCell>
                <TableCell className="hidden font-mono text-xs text-muted-foreground md:table-cell">
                  {hook.events.join(', ')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      </div>
    </PageFrame>
  );
}

function WebhookDetail({ id }: { id: string }) {
  const [hook, setHook] = useState<WebhookDto | null>(null);
  const [deliveries, setDeliveries] = useState<DeliveryDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [events, setEvents] = useState<string[]>([]);
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [dto, logs] = await Promise.all([
        apiFetch<WebhookDto>(v1Path(`/webhooks/${id}`)),
        apiFetch<{ items: DeliveryDto[] }>(v1Path(`/webhooks/${id}/deliveries?limit=50&offset=0`)),
      ]);
      setHook(dto);
      setUrl(dto.url);
      setEvents(dto.events);
      setDeliveries(logs.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load webhook');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  function toggleEvent(event: string) {
    setEvents((current) => (
      current.includes(event) ? current.filter((e) => e !== event) : [...current, event]
    ));
  }

  async function onSave(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const dto = await apiFetch<WebhookDto>(v1Path(`/webhooks/${id}`), {
        method: 'PATCH',
        body: JSON.stringify({ url: url.trim(), events }),
      });
      setHook(dto);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function onRotate() {
    setError(null);
    try {
      const res = await apiFetch<{ secret: string }>(v1Path(`/webhooks/${id}/rotate-secret`), { method: 'POST' });
      setSecret(res.secret);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rotate failed');
    }
  }

  async function onToggleActive() {
    if (!hook) return;
    setError(null);
    try {
      const dto = await apiFetch<WebhookDto>(v1Path(`/webhooks/${id}`), {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !hook.is_active }),
      });
      setHook(dto);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  }

  async function onDelete() {
    setError(null);
    try {
      await apiFetch(v1Path(`/webhooks/${id}`), { method: 'DELETE' });
      window.location.assign('/settings/webhooks');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <PageFrame>
      <PageTitle title="Webhook" />
      {error ? <ErrorAlert>{error}</ErrorAlert> : null}
      {secret ? (
        <div className="mb-4 border-t py-3">
          <p className="text-sm font-medium">New secret. Copy it now.</p>
          <code className="mt-1 block break-all font-mono text-xs">{secret}</code>
          <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setSecret(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}
      {!hook ? (
        <LoadingRows columns={2} />
      ) : (
        <form onSubmit={onSave} className="mb-6 max-w-2xl space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="detail-url">URL</Label>
            <Input id="detail-url" value={url} onChange={(e) => setUrl(e.target.value)} />
          </div>
          <EventPicker selected={events} onToggle={toggleEvent} />
          <p className="text-xs text-muted-foreground">
            {hook.is_active ? 'Active' : 'Disabled'}
            {hook.disabled_at
              ? ` (disabled ${formatTimestampDateTime(hook.disabled_at) ?? hook.disabled_at})`
              : ''}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => void onRotate()}>Rotate secret</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => void onToggleActive()}>
              {hook.is_active ? 'Disable' : 'Enable'}
            </Button>
            <Button type="button" size="sm" variant="destructive" onClick={() => void onDelete()}>Delete</Button>
          </div>
        </form>
      )}
      <h2 className="mb-3 text-lg font-semibold tracking-tight">Deliveries</h2>
      {deliveries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No deliveries yet.</p>
      ) : (
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-0">Event</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead className="hidden w-28 md:table-cell">Attempt</TableHead>
              <TableHead className="hidden w-44 md:table-cell">When</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deliveries.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <MonoKey>{row.event}</MonoKey>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {row.status}
                  {row.response_status != null ? ` HTTP ${row.response_status}` : ''}
                </TableCell>
                <TableCell className="hidden font-mono text-xs tabular-nums text-muted-foreground md:table-cell">
                  {row.attempt}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <MonoKey>{formatTimestampDateTime(row.created_at) ?? '-'}</MonoKey>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </PageFrame>
  );
}
